import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import React, { Suspense } from 'react';

const HeroScene = React.lazy(() => import('./components/HeroScene'));

import Services from './components/ServicesOS';
import Process from './components/Process';
import CaseStudies from './components/CaseStudies';
import InteractiveContact from './components/InteractiveContact';
import ClickSpark from './components/ClickSpark';
import Magnet from './components/Magnet';
import ScrollVelocity from './components/ScrollVelocity';
import Pricing from './components/Pricing';
import FAQSection from './components/FAQSection';
import ThemeToggle from './components/ThemeToggle';
import Vault from './components/Vault';
import BlogPost from './pages/BlogPost';
import Work from './pages/Work';
import ProjectDetail from './pages/ProjectDetail';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import FAQ from './components/FAQ';
import About from './components/About';
import SEO from './components/SEO';


import { Menu, X } from 'lucide-react';

function App() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showScrollHint, setShowScrollHint] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const words = ["DESIGN", "BUILD", "SHIP", "CREATE", "ITERATE"];
  const [currentWordIndex, setCurrentWordIndex] = useState(0);

  // Scroll to top and close menu on route change, but respect hash
  useEffect(() => {
    const handleScroll = () => {
      if (location.hash) {
        const id = location.hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: "auto", block: "start" });
        }
      } else {
        window.scrollTo(0, 0);
      }
      setIsMenuOpen(false);
    };

    handleScroll();
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const progress = Math.min(scrollPosition / (windowHeight * 0.4), 1);

      setScrollProgress(progress);

      if (progress > 0.1) {
        setShowScrollHint(false);
      }
    };

    const wordInterval = setInterval(() => {
      setCurrentWordIndex((prev) => (prev + 1) % words.length);
    }, 3000);

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearInterval(wordInterval);
    };
  }, []);

  const navigate = useNavigate();

  const isHome = location.pathname === '/';

  // Programmatic scroll-to-section that works from any page
  const scrollToSection = (sectionId: string) => {
    setIsMenuOpen(false);
    if (isHome) {
      // Already on home — just scroll
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Navigate to home first, then scroll after render
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const menuItems = [
    { label: 'Services', section: 'services' },
    { label: 'Process', section: 'process' },
    { label: 'Work', section: 'work' },
    { label: 'About', section: null, route: '/about' },
    { label: 'Blog', section: null, route: '/blog' },
    { label: 'FAQ', section: 'faq' },
    { label: 'Contact', section: 'contact' },
  ];

  return (
    <ClickSpark sparkColor="var(--ink-black)" sparkSize={10} sparkRadius={15} sparkCount={8} duration={400}>
      <div className="relative min-h-screen overflow-x-hidden selection:bg-black selection:text-white">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-[100] p-6 flex justify-between items-center text-ink">
          <Magnet padding={60} magnetStrength={3}>
            <Link
              to="/"
              onClick={(e) => {
                if (location.pathname === '/') {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
              className="text-2xl font-bold tracking-tighter cursor-pointer hover:bg-[var(--ink-black)] hover:text-[var(--bg-paper)] px-3 py-1 rounded-full transition-all duration-300"
            >
              QROMA
            </Link>
          </Magnet>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Magnet padding={60} magnetStrength={3}>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label={isMenuOpen ? "Close menu" : "Open menu"}
                className="p-2.5 z-50 bg-[var(--ink-black)] text-[var(--bg-paper)] border-2 border-[var(--ink-black)] rounded-full hover:bg-transparent hover:text-[var(--ink-black)] transition-all duration-300"
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </Magnet>
          </div>
        </nav>

        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="fixed inset-4 z-40 bg-[var(--bg-paper)] border-2 border-[var(--ink-black)] rounded-[2rem] flex flex-col items-center justify-center shadow-[8px_8px_0px_0px_var(--shadow-color)]"
            >
              {/* Overlay Texture */}
              <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,...')] pointer-events-none" />

              <div className="flex flex-col gap-4 text-center relative z-10">
                {menuItems.map((item, index) => (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    transition={{ delay: 0.1 * index }}
                  >
                    {item.section ? (
                      <button
                        onClick={() => scrollToSection(item.section!)}
                        className="block w-full text-4xl md:text-6xl font-bold tracking-tighter font-clean px-8 py-3 rounded-xl bg-[var(--ink-black)] text-[var(--bg-paper)] border-2 border-[var(--ink-black)] hover:bg-transparent hover:text-[var(--ink-black)] transition-all duration-300"
                      >
                        {item.label}
                      </button>
                    ) : (
                      <Link
                        to={item.route!}
                        onClick={() => setIsMenuOpen(false)}
                        className="block text-4xl md:text-6xl font-bold tracking-tighter font-clean px-8 py-3 rounded-xl bg-[var(--ink-black)] text-[var(--bg-paper)] border-2 border-[var(--ink-black)] hover:bg-transparent hover:text-[var(--ink-black)] transition-all duration-300"
                      >
                        {item.label}
                      </Link>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <Routes>
          <Route path="/" element={
            <>
              {/* Hero Section with 3D Planet */}
              <div className="relative h-screen w-full">
                <div className="absolute inset-0 z-0">
                  <Suspense fallback={null}>
                    <HeroScene scrollProgress={scrollProgress} />
                  </Suspense>
                </div>

                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center pointer-events-none">
                  <div className="h-[12vw] flex items-center justify-center overflow-hidden">
                    <AnimatePresence mode="wait">
                      <motion.span
                        key={words[currentWordIndex]}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className="text-[12vw] leading-none font-bold text-ink tracking-tighter mix-blend-overlay text-center absolute"
                        role="heading"
                        aria-level={2}
                      >
                        {words[currentWordIndex]}
                      </motion.span>
                    </AnimatePresence>
                  </div>
                </div>

                <AnimatePresence>
                  {showScrollHint && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="absolute bottom-12 left-1/2 -translate-x-1/2 text-sm font-sketch tracking-widest"
                    >
                      <Magnet padding={100} magnetStrength={2}>
                        SCROLL TO EXPLORE
                      </Magnet>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <SEO
                title="Qroma | Product & Software Studio in Durban, South Africa"
                description="Qroma is a development and creative studio in Durban. We build our own products — Halaq, The Ledger, VidMetrics — and take on a small number of client builds each year."
                canonical="https://www.qroma.digital/"
              />

              <div className="relative z-10 bg-paper">
                <Services />
                <Process />
                <Pricing />
                <CaseStudies />

                {/* ScrollVelocity Marquee between CaseStudies and Contact */}
                <ScrollVelocity
                  texts={['QROMA STUDIO ✦ ', 'Design · Build · Ship · ']}
                  velocity={80}
                  scrollerClassNames={['velocity-scroller', 'velocity-scroller velocity-scroller-sketch']}
                />

                <div id="faq">
                  <FAQSection />
                </div>
                <InteractiveContact />
              </div>
            </>
          } />

          <Route path="/work" element={
            <>
              <SEO
                title="Work | Products, Client Builds & Experiments — Qroma"
                description="The full Qroma archive: products we own, software we ship for clients, infrastructure, mobile apps and the smaller experiments from the lab."
                canonical="https://www.qroma.digital/work"
              />
              <Work />
            </>
          } />
          <Route path="/work/:slug" element={<ProjectDetail />} />

          <Route path="/blog" element={
            <>
              <SEO
                title="The Vault | Notes from the Qroma Studio"
                description="Notes on building software, design and automation from the Qroma studio in Durban."
                canonical="https://www.qroma.digital/blog"
              />
              <Vault />
            </>
          } />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/privacy" element={
            <>
              <SEO
                title="Privacy Policy | Qroma Digital"
                description="Read our Privacy Policy to learn how Qroma Digital protects your personal information."
                canonical="https://www.qroma.digital/privacy"
              />
              <PrivacyPolicy />
            </>
          } />
          <Route path="/terms" element={
            <>
              <SEO
                title="Terms of Service | Qroma Digital"
                description="Terms of Service and conditions for working with Qroma Digital."
                canonical="https://www.qroma.digital/terms"
              />
              <TermsOfService />
            </>
          } />
          <Route path="/faq" element={
            <>
              <SEO
                title="FAQ | Working With Qroma"
                description="How Qroma works: engagement models, timelines, what we build, who owns the code, and what it costs."
                canonical="https://www.qroma.digital/faq"
              />
              <FAQ />
            </>
          } />
          <Route path="/about" element={
            <>
              <SEO
                title="About | The Qroma Studio"
                description="Qroma is a development and creative studio founded in Durban by Yahya Paruk and Ubaid Desai. We build our own products and take on a small number of client builds."
                canonical="https://www.qroma.digital/about"
              />
              <About />
            </>
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

      </div>
    </ClickSpark>
  );
}

export default App;
